import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, ChevronRight, ArrowLeft, Sparkles, X, ShieldCheck } from 'lucide-react';
import FaceMap from './FaceMap';

const FOOD_TAGS = ['Dairy', 'Sugar', 'Gluten', 'Alcohol', 'Caffeine', 'Processed', 'Spicy', 'Soy', 'Eggs', 'Fried Food', 'Fruit', 'Vegetables', 'Nuts', 'Fish'];

const SKIN_OPTIONS = [
  { value: 0, label: 'Clear', emoji: '✨', sub: 'No lesions', color: 'bg-[#4ADE80]/10 border-[#4ADE80] text-[#159B4D]' },
  { value: 1, label: 'Almost Clear', emoji: '🙂', sub: 'Few comedones', color: 'bg-[#4ADE80]/10 border-[#4ADE80]/50 text-[#159B4D]' },
  { value: 3, label: 'Mild', emoji: '😐', sub: 'Whiteheads, few papules', color: 'bg-[#E5C07B]/10 border-[#E5C07B] text-[#9A7B2E]' },
  { value: 6, label: 'Moderate', emoji: '😣', sub: 'Papules & pustules', color: 'bg-[#A388EE]/10 border-[#A388EE] text-[#A388EE]' },
  { value: 9, label: 'Severe', emoji: '😩', sub: 'Inflamed or cystic', color: 'bg-red-50 border-red-300 text-red-600' },
];

const WATER_OPTIONS = [
  { value: 2, label: '0-2', sub: 'Well below need' },
  { value: 4, label: '3-4', sub: 'Below target' },
  { value: 6, label: '5-6', sub: 'Getting there' },
  { value: 8, label: '7-8', sub: '~2L guideline' },
  { value: 10, label: '9+', sub: 'Optimal' },
];

const STRESS_OPTIONS = [
  { value: 1, label: 'Never', emoji: '😌', sub: 'In control all day' },
  { value: 3, label: 'Almost Never', emoji: '🙂', sub: 'A brief moment' },
  { value: 5, label: 'Sometimes', emoji: '😐', sub: 'On and off' },
  { value: 7, label: 'Often', emoji: '😰', sub: 'Much of the day' },
  { value: 9, label: 'Very Often', emoji: '🤯', sub: 'Overwhelmed' },
];

const SLEEP_OPTIONS = [
  { value: 4, label: '<5h', sub: 'Very poor' },
  { value: 5.5, label: '5-6h', sub: 'Poor' },
  { value: 7, label: '6-7h', sub: 'Fair' },
  { value: 8, label: '7-8h', sub: 'Clinical target' },
  { value: 9, label: '8h+', sub: 'Optimal' },
];

const BLOAT_OPTIONS = [
  { value: 0, label: 'None', emoji: '😊', sub: 'No discomfort' },
  { value: 2, label: 'Mild', emoji: '🙂', sub: 'Noticeable, not bothersome' },
  { value: 5, label: 'Moderate', emoji: '😐', sub: 'Uncomfortable' },
  { value: 7, label: 'Severe', emoji: '😣', sub: 'Limits activities' },
  { value: 10, label: 'Very Severe', emoji: '🫠', sub: 'Constant, distressing' },
];

const ProgressBar = ({ current, total }) => (
  <div className="w-full h-1.5 bg-[#E5D0C9] rounded-full overflow-hidden">
    <div
      className="h-full bg-gradient-to-r from-[#4ADE80] to-[#FF6B4A] rounded-full transition-all duration-500 ease-out"
      style={{ width: `${(current / total) * 100}%` }}
    />
  </div>
);

const QuestionBubble = ({ text }) => (
  <div className="flex items-start gap-3 mb-6 animate-fade-in">
    <div className="w-8 h-8 rounded-full bg-[#FF6B4A] flex items-center justify-center flex-shrink-0 mt-0.5">
      <Sparkles className="w-4 h-4 text-white" />
    </div>
    <div className="bg-[#F4F2EE] px-4 py-3 rounded-2xl rounded-bl-md max-w-[85%]">
      <p className="text-sm text-[#1A1514] font-medium">{text}</p>
    </div>
  </div>
);

const OptionGrid = ({ options, selected, onSelect, columns = 'grid-cols-3' }) => (
  <div className={`grid ${columns} gap-2 animate-fade-in`}>
    {options.map((opt) => {
      const isSelected = selected === opt.value;
      return (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
            isSelected
              ? (opt.color || 'bg-[#FF6B4A]/10 border-[#FF6B4A] text-[#FF6B4A]')
              : 'bg-white border-[#E5D0C9] text-[#8C7A76] hover:border-[#4ADE80] hover:bg-[#FFF2EC]'
          }`}
          data-testid={`opt-${opt.label.replace(/\s/g, '-').toLowerCase()}`}
        >
          {opt.emoji && <span className="text-xl block mb-1">{opt.emoji}</span>}
          <span className="text-sm font-semibold block">{opt.label}</span>
          {opt.sub && <span className="text-[10px] block mt-0.5 opacity-60">{opt.sub}</span>}
        </button>
      );
    })}
  </div>
);

const FoodPicker = ({ selected, onToggle, customFood, setCustomFood, addCustom }) => (
  <div className="space-y-3 animate-fade-in">
    <div className="flex flex-wrap gap-2">
      {FOOD_TAGS.map((food) => {
        const isActive = selected.includes(food);
        return (
          <button
            key={food}
            onClick={() => onToggle(food)}
            className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
              isActive
                ? 'bg-[#FF6B4A] text-white border-[#FF6B4A]'
                : 'bg-white text-[#8C7A76] border-[#E5D0C9] hover:border-[#FF6B4A]'
            }`}
            data-testid={`food-${food.toLowerCase()}`}
          >
            {food} {isActive && '✓'}
          </button>
        );
      })}
    </div>
    <div className="flex gap-2">
      <input
        value={customFood}
        onChange={(e) => setCustomFood(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && addCustom()}
        placeholder="Add other food..."
        className="flex-1 px-3 py-2 border border-[#E5D0C9] rounded-xl text-sm focus:outline-none focus:border-[#FF6B4A]"
        data-testid="custom-food-input"
      />
      {customFood && (
        <button onClick={addCustom} className="px-3 py-2 bg-[#FF6B4A] text-white rounded-xl text-sm font-semibold">Add</button>
      )}
    </div>
    {selected.filter((f) => !FOOD_TAGS.includes(f)).length > 0 && (
      <div className="flex flex-wrap gap-1">
        {selected.filter((f) => !FOOD_TAGS.includes(f)).map((f) => (
          <span key={f} className="px-2 py-1 bg-[#FF6B4A] text-white rounded-full text-xs flex items-center gap-1">
            {f} <button onClick={() => onToggle(f)}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    )}
  </div>
);

const STEPS = [
  { id: 'skin', question: "How does your skin look today?", clinical: 'Anchored to the IGA scale — the FDA-standard measure used in dermatology trials.' },
  { id: 'breakouts', question: 'Where are your breakouts? (tap all that apply)', clinical: 'Facial mapping: jawline/chin lesions are the clinical hallmark of hormonal acne.' },
  { id: 'water', question: 'How many glasses of water did you drink today?', clinical: 'EFSA guideline: ~2L/day for women (≈8 glasses) supports skin barrier & gut lining.' },
  { id: 'bloating', question: 'How much has bloating or gut discomfort bothered you today?', clinical: 'Anchored to the GSRS — the validated gastrointestinal symptom rating scale.' },
  { id: 'stress', question: 'Today, how often did you feel unable to control or cope with things?', clinical: 'Adapted from the Perceived Stress Scale (PSS) — the most-used clinical stress measure.' },
  { id: 'sleep', question: 'How many hours did you actually sleep last night? (asleep, not just in bed)', clinical: 'From the Pittsburgh Sleep Quality Index (PSQI): <6h of true sleep raises inflammatory markers.' },
  { id: 'food', question: 'Which of these did you eat today? (tap all)', clinical: 'These tags map to peer-reviewed skin & gut triggers (dairy, high-glycemic, alcohol).' },
];

const CYCLE_STEP = { id: 'cycle', question: 'What day of your cycle are you on?', clinical: null };

const DailyLogTab = ({ todayLog, onSave, loading, defaults, trackCycle = true }) => {
  const normalizeLocations = (loc) => {
    if (Array.isArray(loc)) return Object.fromEntries(loc.map((k) => [k, true]));
    return loc && typeof loc === 'object' ? loc : {};
  };
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    acne_severity: null,
    acne_locations: normalizeLocations(defaults.acneLocations),
    water_glasses: null,
    bloating_level: null,
    stress_level: null,
    sleep_hours: null,
    food_intake: [],
    cycle_day: defaults.cycleDay || null,
    is_period: defaults.isPeriod || false,
  });
  const [customFood, setCustomFood] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const scrollRef = useRef(null);

  const steps = trackCycle ? [...STEPS, CYCLE_STEP] : STEPS;
  const totalSteps = steps.length;
  const currentStep = steps[step];

  // Sync from existing log
  useEffect(() => {
    if (!todayLog) return;
    setAnswers({
      acne_severity: todayLog.acne_severity ?? null,
      acne_locations: normalizeLocations(todayLog.acne_locations),
      water_glasses: todayLog.water_glasses ?? null,
      bloating_level: todayLog.bloating_level ?? null,
      stress_level: todayLog.stress_level ?? null,
      sleep_hours: todayLog.sleep_hours ?? null,
      food_intake: todayLog.food_intake || [],
      cycle_day: todayLog.cycle_day,
      is_period: todayLog.is_period || false,
    });
    setSubmitted(true);
  }, [todayLog]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleZone = (key) => {
    setAnswers((prev) => ({
      ...prev,
      acne_locations: { ...prev.acne_locations, [key]: !prev.acne_locations[key] }
    }));
  };

  const toggleFood = (food) => {
    setAnswers((prev) => ({
      ...prev,
      food_intake: prev.food_intake.includes(food)
        ? prev.food_intake.filter((f) => f !== food)
        : [...prev.food_intake, food]
    }));
  };

  const addCustomFood = () => {
    if (customFood.trim()) {
      toggleFood(customFood.trim());
      setCustomFood('');
    }
  };

  const canProceed = () => {
    if (currentStep.id === 'skin') return answers.acne_severity !== null;
    if (currentStep.id === 'breakouts') return true;
    if (currentStep.id === 'water') return answers.water_glasses !== null;
    if (currentStep.id === 'bloating') return answers.bloating_level !== null;
    if (currentStep.id === 'stress') return answers.stress_level !== null;
    if (currentStep.id === 'sleep') return answers.sleep_hours !== null;
    if (currentStep.id === 'food') return true;
    if (currentStep.id === 'cycle') return true;
    return true;
  };

  const goNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      handleSave();
    }
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSave = () => {
    onSave({
      acne_locations: answers.acne_locations,
      acne_severity: answers.acne_severity ?? 0,
      bloating_level: answers.bloating_level ?? 0,
      cycle_day: answers.cycle_day,
      is_period: answers.is_period,
      food_intake: answers.food_intake,
      water_glasses: answers.water_glasses ?? 0,
      stress_level: answers.stress_level ?? 0,
      sleep_hours: answers.sleep_hours ?? 7,
    });
    setSubmitted(true);
  };

  // Already logged view
  if (submitted && todayLog) {
    return (
      <Card className="card-elevated border-0" data-testid="log-complete">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#4ADE80]/20 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[#FF6B4A]" />
          </div>
          <h3 className="font-heading text-xl font-bold text-[#1A1514] mb-2">Today's log is saved!</h3>
          <p className="text-sm text-[#8C7A76] mb-6">Your detox score and garden have been updated.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-[#FFF2EC] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-[#1A1514]">{todayLog.acne_severity}/10</p>
              <p className="text-[10px] text-[#8C7A76]">Skin</p>
            </div>
            <div className="bg-[#FFF2EC] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-[#1A1514]">{todayLog.water_glasses}</p>
              <p className="text-[10px] text-[#8C7A76]">Water</p>
            </div>
            <div className="bg-[#FFF2EC] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-[#1A1514]">{todayLog.stress_level}/10</p>
              <p className="text-[10px] text-[#8C7A76]">Stress</p>
            </div>
            <div className="bg-[#FFF2EC] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-[#1A1514]">{todayLog.sleep_hours}h</p>
              <p className="text-[10px] text-[#8C7A76]">Sleep</p>
            </div>
          </div>
          <Button onClick={() => { setSubmitted(false); setStep(0); }} variant="outline" className="border-[#E5D0C9] rounded-full" data-testid="edit-log-btn">
            Edit Today's Log
          </Button>
          {trackCycle && !todayLog.cycle_day && (
            <div className="flex items-start gap-2 bg-[#A388EE]/10 border border-[#A388EE]/30 rounded-xl px-3 py-2.5 mt-4 text-left" data-testid="cycle-nudge-complete">
              <Sparkles className="w-4 h-4 text-[#A388EE] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#1A1514] leading-relaxed">
                <span className="font-semibold">Add your cycle day to unlock breakout predictions</span> — we'll warn you ~3 days before your luteal window. Tap "Edit Today's Log" above.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-elevated border-0 max-w-lg mx-auto" data-testid="quick-log" ref={scrollRef}>
      <CardContent className="p-6">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#8C7A76] font-medium">Question {step + 1} of {totalSteps}</span>
            <span className="text-xs text-[#FF6B4A] font-semibold">{Math.round(((step + 1) / totalSteps) * 100)}%</span>
          </div>
          <ProgressBar current={step + 1} total={totalSteps} />
        </div>

        {/* Question */}
        <QuestionBubble text={currentStep.question} />
        {currentStep.clinical && (
          <div className="flex items-start gap-2 -mt-3 mb-5 ml-11 animate-fade-in">
            <ShieldCheck className="w-3.5 h-3.5 text-[#4ADE80] flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-[#8C7A76] italic leading-relaxed" data-testid="clinical-note">{currentStep.clinical}</p>
          </div>
        )}

        {/* Answer area */}
        <div className="mb-6">
          {currentStep.id === 'skin' && (
            <OptionGrid options={SKIN_OPTIONS} selected={answers.acne_severity} onSelect={(v) => { setAnswer('acne_severity', v); }} columns="grid-cols-5 sm:grid-cols-5" />
          )}
          {currentStep.id === 'breakouts' && (
            <FaceMap selected={answers.acne_locations} onToggle={toggleZone} />
          )}
          {currentStep.id === 'water' && (
            <OptionGrid options={WATER_OPTIONS} selected={answers.water_glasses} onSelect={(v) => setAnswer('water_glasses', v)} columns="grid-cols-5" />
          )}
          {currentStep.id === 'bloating' && (
            <OptionGrid options={BLOAT_OPTIONS} selected={answers.bloating_level} onSelect={(v) => setAnswer('bloating_level', v)} columns="grid-cols-5 sm:grid-cols-5" />
          )}
          {currentStep.id === 'stress' && (
            <OptionGrid options={STRESS_OPTIONS} selected={answers.stress_level} onSelect={(v) => setAnswer('stress_level', v)} columns="grid-cols-5" />
          )}
          {currentStep.id === 'sleep' && (
            <OptionGrid options={SLEEP_OPTIONS} selected={answers.sleep_hours} onSelect={(v) => setAnswer('sleep_hours', v)} columns="grid-cols-5" />
          )}
          {currentStep.id === 'food' && (
            <FoodPicker selected={answers.food_intake} onToggle={toggleFood} customFood={customFood} setCustomFood={setCustomFood} addCustom={addCustomFood} />
          )}
          {currentStep.id === 'cycle' && (
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-start gap-2 bg-[#A388EE]/10 border border-[#A388EE]/30 rounded-xl px-3 py-2.5" data-testid="cycle-nudge">
                <Sparkles className="w-4 h-4 text-[#A388EE] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#1A1514] leading-relaxed">
                  <span className="font-semibold">Unlocks breakout predictions:</span> with your cycle day, InnerGlow warns you ~3 days before your luteal breakout window.
                </p>
              </div>
              <div className="flex gap-2">
                <input
                  type="number" min={1} max={35}
                  value={answers.cycle_day || ''}
                  onChange={(e) => setAnswer('cycle_day', parseInt(e.target.value) || null)}
                  placeholder="Cycle day (e.g. 14)"
                  className="flex-1 px-3 py-2.5 border border-[#E5D0C9] rounded-xl text-sm focus:outline-none focus:border-[#FF6B4A]"
                  data-testid="cycle-day-quick"
                />
                <button
                  onClick={() => setAnswer('is_period', !answers.is_period)}
                  className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                    answers.is_period ? 'bg-[#A388EE] text-white border-[#A388EE]' : 'bg-white text-[#8C7A76] border-[#E5D0C9]'
                  }`}
                  data-testid="period-quick"
                >
                  {answers.is_period && <Check className="w-3.5 h-3.5 inline mr-1" />}On Period
                </button>
              </div>
              <p className="text-xs text-[#8C7A76] text-center">Leave blank to skip</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-3">
          {step > 0 && (
            <Button onClick={goBack} variant="outline" className="border-[#E5D0C9] rounded-full px-4" data-testid="log-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <Button
            onClick={goNext}
            disabled={!canProceed() || loading}
            className="flex-1 bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full py-5 font-semibold"
            data-testid="log-next-btn"
          >
            {loading ? 'Saving...' : step === totalSteps - 1 ? (
              <>Save Log <Check className="w-4 h-4 ml-2" /></>
            ) : (
              <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyLogTab;
